// The scanned page (docs/05 Flow A): who you are talking to, then straight into the call.
// The call starts on load; if the browser holds sound back until a tap (iOS), one large
// button completes it. A denied mic falls back to text chat with the same agent.
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { CardActionMsg, ServerMessage, SessionDescriptor, TextInputMsg } from "@stc/proto";
import { LiveKitTransport } from "../transport/livekit-transport";
import type { CallTransport } from "../transport/call-transport";
import { Kolam, type OrbState } from "./Kolam";
import { Card, type CardMsg } from "./Cards";
import { Ringback } from "./ringback";
import "./caller.css";

interface Placement {
  code: string;
  label: string;
  business: { name: string; logo_url: string | null };
  agent: { name: string; description: string; language: string };
  app: { android: string; ios: string };
}

type Phase =
  | { kind: "loading" }
  | { kind: "gone"; message: string }
  | { kind: "starting" }
  | { kind: "tap" } // connected, but the browser needs a tap before it plays sound
  | { kind: "live" }
  | { kind: "mic-denied" }
  | { kind: "ended"; reason: string }
  | { kind: "error"; message: string };

interface Caption {
  role: "caller" | "agent";
  text: string;
  final: boolean;
}

const STATE_TEXT: Record<OrbState, string> = {
  connecting: "Connecting",
  listening: "Listening",
  thinking: "Thinking",
  speaking: "Speaking",
  ended: "Call ended",
};

const END_TEXT: Record<string, string> = {
  caller_hangup: "You ended the call.",
  agent_done: "The call is complete.",
  idle_timeout: "The call ended after a quiet minute.",
  max_duration: "The call reached its time limit.",
  error: "The call dropped because of a problem on our side.",
};

function monogram(name: string): string {
  const w = name.trim().split(/\s+/);
  return ((w[0]?.[0] ?? "") + (w[1]?.[0] ?? "")).toUpperCase() || "?";
}

function storeLink(app: Placement["app"]): string {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return app.android;
  if (/iphone|ipad|ipod/i.test(ua)) return app.ios;
  return app.android || app.ios;
}

/**
 * The caller's live transcript is revised as they speak: a partial replaces the previous
 * partial. The agent's reply arrives sentence by sentence: each sentence is appended to the
 * reply's bubble, and a final caption (text mode) replaces the bubble with the full reply.
 */
function addCaption(list: Caption[], c: Caption): Caption[] {
  const last = list[list.length - 1];
  if (last && last.role === c.role && !last.final) {
    const text = c.role === "agent" && !c.final ? `${last.text} ${c.text}` : c.text;
    return [...list.slice(0, -1), { ...c, text }];
  }
  return [...list, c].slice(-8);
}

const encode = (msg: object) => new TextEncoder().encode(JSON.stringify(msg));
const id = () => crypto.randomUUID();

export function CallerPage({ code }: { code: string }) {
  const [place, setPlace] = useState<Placement | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [orb, setOrb] = useState<OrbState>("connecting");
  const [level, setLevel] = useState(0);
  const [captions, setCaptions] = useState<Caption[]>([]);
  const [cards, setCards] = useState<CardMsg[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState("");
  const [textMode, setTextMode] = useState(false);
  const transport = useRef<CallTransport | null>(null);
  const started = useRef(false);
  const ring = useRef(new Ringback());

  useEffect(() => {
    fetch(`/v1/placements/${encodeURIComponent(code)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error ?? "This code is no longer active.");
        return r.json() as Promise<Placement>;
      })
      .then((p) => {
        setPlace(p);
        document.title = `${p.business.name}: ${p.agent.name}`;
        setPhase({ kind: "starting" });
      })
      .catch((e: Error) => setPhase({ kind: "gone", message: e.message }));
  }, [code]);

  // Auto-call once the page knows who it is talking to.
  useEffect(() => {
    if (phase.kind === "starting" && !started.current) {
      started.current = true;
      void start(false);
    }
  }, [phase.kind]);

  useEffect(() => {
    const t = setInterval(() => transport.current && setLevel(transport.current.levels().remote), 120);
    return () => {
      clearInterval(t);
      ring.current.stop();
      void transport.current?.close();
    };
  }, []);

  async function start(text: boolean) {
    await transport.current?.close();
    const t = new LiveKitTransport();
    transport.current = t;
    setTextMode(text);
    setCaptions([]);
    setCards([]);
    setOrb("connecting");
    setPhase({ kind: "starting" });
    // Inside a tap this unlocks audio; on load it may be refused, which `onAudioPlayback` reports.
    void t.unlockAudio().catch(() => {});
    // Ring until the agent is on the line: its first state message or its first audio.
    ring.current.stop();
    if (!text) ring.current.start();
    t.onRemoteAudio((e) => e.playing && ring.current.stop());

    t.onData((payload) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(new TextDecoder().decode(payload)) as ServerMessage;
      } catch {
        return;
      }
      if (msg.type === "state") {
        ring.current.stop();
        setOrb(msg.state);
      }
      else if (msg.type === "caption") setCaptions((l) => addCaption(l, { role: msg.role, text: msg.text, final: msg.final }));
      else if (msg.type === "card.show") setCards((l) => [...l.filter((c) => c.card_id !== msg.card_id), { card_id: msg.card_id, kind: msg.kind, data: msg.data }]);
      else if (msg.type === "card.update") setCards((l) => l.map((c) => (c.card_id === msg.card_id ? { ...c, data: { ...c.data, ...msg.data } } : c)));
      else if (msg.type === "card.dismiss") setCards((l) => l.filter((c) => c.card_id !== msg.card_id));
      else if (msg.type === "session.end") {
        setOrb("ended");
        setPhase({ kind: "ended", reason: msg.reason });
        ring.current.stop();
        void t.close();
      }
    });
    t.onAudioPlayback((allowed) => setPhase((p) => (allowed ? (p.kind === "tap" ? { kind: "live" } : p) : p.kind === "live" ? { kind: "tap" } : p)));

    try {
      const res = await fetch("/v1/sessions", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ placement: code, ...(text ? { text: true } : {}) }),
      });
      if (!res.ok) throw new Error(res.status === 404 ? "This code is no longer active." : "The front desk could not be reached. Try again in a moment.");
      const descriptor = (await res.json()) as SessionDescriptor;
      await t.connect(descriptor);
      if (!text) {
        try {
          await t.enableMic();
        } catch {
          ring.current.stop();
          setPhase({ kind: "mic-denied" });
          await t.close();
          return;
        }
      }
      setPhase(text || t.canPlayAudio() ? { kind: "live" } : { kind: "tap" });
    } catch (e) {
      ring.current.stop();
      setPhase({ kind: "error", message: e instanceof Error ? e.message : "The call could not start." });
    }
  }

  async function tapToHear() {
    ring.current.resume();
    await transport.current?.unlockAudio().catch(() => {});
    if (transport.current?.canPlayAudio()) setPhase({ kind: "live" });
  }

  async function pick(cardId: string, value: string) {
    setPicked((p) => ({ ...p, [cardId]: value }));
    const msg: CardActionMsg = { v: 1, id: id(), ts: Date.now(), type: "card.action", card_id: cardId, action: "pick", value };
    await transport.current?.send(encode(msg));
  }

  async function sendText(e: Event) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !transport.current) return;
    const msg: TextInputMsg = { v: 1, id: id(), ts: Date.now(), type: "text.input", text };
    await transport.current.send(encode(msg));
    setDraft("");
  }

  async function hangUp() {
    ring.current.stop();
    await transport.current?.send(encode({ v: 1, id: id(), ts: Date.now(), type: "hangup" }));
    await transport.current?.close();
    setOrb("ended");
    setPhase({ kind: "ended", reason: "caller_hangup" });
  }

  const store = useMemo(() => (place ? storeLink(place.app) : ""), [place]);
  // A tapped slot reaches the agent as "20:30"; the caller sees the label they tapped.
  const slotLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of cards) for (const o of (c.data.options as { label: string; value: string }[] | undefined) ?? []) m.set(o.value, o.label);
    return m;
  }, [cards]);
  const inCall = phase.kind === "live" || phase.kind === "tap" || phase.kind === "starting";

  if (phase.kind === "loading") return <main class="caller" aria-busy="true" />;
  if (phase.kind === "gone" || !place) {
    return (
      <main class="caller">
        <div class="notice">
          <h1>This code is not active</h1>
          <p>{phase.kind === "gone" ? phase.message : "Ask the staff for a current code."}</p>
        </div>
      </main>
    );
  }

  return (
    <main class="caller">
      <header class="who">
        {place.business.logo_url ? (
          <img class="logo" src={place.business.logo_url} alt="" width={56} height={56} />
        ) : (
          <span class="logo monogram" aria-hidden="true">
            {monogram(place.business.name)}
          </span>
        )}
        <div>
          <h1>{place.business.name}</h1>
          <p class="agent-name">{place.agent.name}</p>
        </div>
      </header>
      <p class="about">{place.agent.description}</p>

      <section class={`stage ${phase.kind}`} aria-live="polite">
        <Kolam state={orb} level={level} />
        <p class="status">
          {phase.kind === "starting" && "Connecting to the front desk"}
          {phase.kind === "tap" && "The front desk is on the line"}
          {phase.kind === "live" && (textMode ? "Chatting by text" : STATE_TEXT[orb])}
          {phase.kind === "mic-denied" && "Your microphone is off"}
          {phase.kind === "ended" && (END_TEXT[phase.reason] ?? "The call has ended.")}
          {phase.kind === "error" && phase.message}
        </p>
        {phase.kind === "tap" && (
          <button class="primary big" onClick={tapToHear}>
            Tap to talk
          </button>
        )}
        {phase.kind === "mic-denied" && (
          <div class="choices">
            <p>Allow the microphone in your browser to talk, or type instead.</p>
            <button class="primary" onClick={() => void start(false)}>
              Try the microphone again
            </button>
            <button class="quiet" onClick={() => void start(true)}>
              Chat by text
            </button>
          </div>
        )}
        {(phase.kind === "ended" || phase.kind === "error") && (
          <button class="primary" onClick={() => void start(false)}>
            Call again
          </button>
        )}
      </section>

      {captions.length > 0 && (
        <ol class="captions" aria-label="Conversation">
          {captions.map((c, i) => (
            <li key={i} class={`from-${c.role} ${c.role === "caller" && !c.final ? "partial" : ""}`}>
              {c.role === "caller" ? (slotLabels.get(c.text) ?? c.text) : c.text}
            </li>
          ))}
        </ol>
      )}

      {cards.map((c) => (
        <Card key={c.card_id} card={c} {...(picked[c.card_id] ? { picked: picked[c.card_id] } : {})} onPick={(cid, v) => void pick(cid, v)} />
      ))}

      {inCall && (
        <form class="compose" onSubmit={sendText}>
          <input
            value={draft}
            onInput={(e) => setDraft(e.currentTarget.value)}
            placeholder={textMode ? "Type your message" : "Or type here"}
            aria-label="Message"
            enterkeyhint="send"
          />
          <button class="primary" type="submit" disabled={!draft.trim()}>
            Send
          </button>
        </form>
      )}

      <footer class="foot">
        {inCall && (
          <button class="hangup" onClick={hangUp}>
            End call
          </button>
        )}
        {store && (
          <p class="app">
            <a href={store} rel="noopener">
              Get the app
            </a>{" "}
            for a lighter call that works on more networks.
          </p>
        )}
      </footer>
    </main>
  );
}
